import React, { useEffect, useState } from 'react';
import { ArrowRight, Check, Loader2, MessageSquare, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { aaiApi } from '../../services/aaiApi';
import type { AaiPolicy, FeatureLink, Quiz, QuizResult, TrainingModule } from '../../types/aai';
import { Action, Card, Chip, Drawer, ErrorNote, Kicker, Loading, Modal } from '../../components/aa';
import { FeaturePanelBody, hasFeaturePanel } from './FeaturePanel';

/*
 * The step drawer — and the step itself, done inside it.
 *
 * Continue does not leave for a page. A person who opened a step from a list
 * finishes it and lands back on that list, with their place intact. A linked
 * step is no exception: the platform surface it anchors to opens in a centered
 * modal over the list (see FeaturePanel), at the measure that surface was built
 * for, while the step itself keeps the reading-width drawer. Only a route with
 * no panel entry navigates, and it says so before it does.
 *
 * ON STEP PROGRESS — the platform records one status per step, not per
 * sub-step (/aai/my/training returns 'not_started' | 'completed'). So a
 * finished step shows every sub-step done and an unfinished one marks the
 * first as next. It never claims someone is part-way through, because nothing
 * stores that. The server owns completion and scoring; this drawer only
 * collects and submits.
 */

export const PolicyBody: React.FC = () => {
  const [policy, setPolicy] = useState<AaiPolicy | null>(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let active = true;
    aaiApi.getPolicy()
      .then(({ data }) => { if (active) { setPolicy(data); setState('ready'); } })
      .catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, []);

  if (state === 'loading') return <Loading label="Loading what is allowed" />;
  if (state === 'error') return <ErrorNote message="Could not load your policy." />;
  if (!policy?.body?.trim()) {
    return (
      <p className="m-0 text-[13px] leading-[21.5px] text-ink-dim">
        Your company has not recorded a policy here yet. Until it does, ask your AI champion
        before using AI on anything you would not put in an email.
      </p>
    );
  }
  return (
    <>
      <p className="m-0 whitespace-pre-wrap text-[14px] leading-[23px] text-ink">{policy.body}</p>
      {policy.updated_at && (
        <p className="m-0 mt-4 text-[12.5px] leading-[20.75px] text-ink-dim">
          Last updated {new Date(policy.updated_at).toLocaleDateString()}
          {policy.updated_by && ` by ${policy.updated_by}`}.
        </p>
      )}
    </>
  );
};

const KIND_CHIP: Record<string, string> = {
  quiz: 'Learning',
  reading: 'Learning',
  micro_learning: 'Learning',
  homework: 'Practice',
  link: 'Applied',
};

const SUBSTEP_MARK: Record<string, string> = {
  done: 'bg-ok-dim text-white',
  next: 'bg-ok-soft text-[#3F7A2F]',
  later: 'bg-canvas-inset text-ink-soft',
};

const SUBSTEP_NOTE: Record<string, string> = { done: 'Done', next: 'Next', later: '' };

const subStepState = (index: number, complete: boolean): string => {
  if (complete) return 'done';
  if (index === 0) return 'next';
  return 'later';
};

const SubStep: React.FC<{ index: number; title: string; complete: boolean }> = ({
  index, title, complete,
}) => {
  const state = subStepState(index, complete);
  return (
    <li className="flex items-start gap-3.5 border-b border-line-soft px-5 py-4 last:border-b-0">
      <span className={`mt-px grid h-7 w-7 shrink-0 place-items-center rounded-full font-sans text-[12px] font-semibold tabular-nums ${SUBSTEP_MARK[state]}`}>
        {state === 'done' && <Check size={14} strokeWidth={2.5} />}
        {state !== 'done' && index + 1}
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold leading-[21.5px] text-ink">{title}</span>
        {SUBSTEP_NOTE[state] && (
          <span className="block text-[13px] leading-[21.5px] text-ink-dim">{SUBSTEP_NOTE[state]}</span>
        )}
      </span>
    </li>
  );
};

const Spinner: React.FC<{ busy: boolean }> = ({ busy }) => {
  if (!busy) return null;
  return <Loader2 size={14} className="animate-spin" />;
};

// What the submission box asks for, per the step's declared submit type.
// The anchor's route, or null when the feature is not built yet — in which
// case the step records itself and says nothing was opened.
const liveRoute = (feature: FeatureLink | null | undefined): string | null => {
  if (!feature?.live) return null;
  return feature.route || null;
};

// Only the view holding a whole platform surface needs the wide panel; a step
// read at 960px is a worse read. That surface carries its own heading, so this
// panel's drops to a line of context.
const SUBMIT_PROMPT: Record<string, string> = {
  text: 'Your response',
  link: 'A link to your work',
  none: '',
};

export const StepDrawer: React.FC<{
  module: TrainingModule;
  onClose: () => void;
  /** Called after the server records completion, so the list behind refreshes. */
  onCompleted: () => void;
}> = ({ module, onClose, onCompleted }) => {
  const navigate = useNavigate();
  const [view, setView] = useState('overview');
  const [section, setSection] = useState(0);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [featureRoute, setFeatureRoute] = useState<string | null>(null);

  const complete = module.status === 'completed';
  const sections = module.body || [];
  const subSteps = sections.map((s) => s.h).filter(Boolean);
  const submitType = module.submit_type || 'none';

  const finish = async (submission?: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await aaiApi.completeStep(module.id, submission);
      onCompleted();
      setView('done');
    } catch {
      setError('Could not record that. Nothing was saved, so you can try again.');
    } finally {
      setBusy(false);
    }
  };

  const openQuiz = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await aaiApi.getQuiz(module.id);
      setQuiz(data);
      setAnswers({});
      setView('quiz');
    } catch {
      setError('Could not open this quiz.');
    } finally {
      setBusy(false);
    }
  };

  const submitQuiz = async () => {
    if (!quiz) return;
    setBusy(true);
    setError(null);
    try {
      const ordered = quiz.questions.map((_, i) => answers[i] ?? -1);
      const { data } = await aaiApi.submitQuiz(quiz.id, ordered);
      setResult(data);
      onCompleted();
      setView('result');
    } catch {
      setError('Could not submit your answers. Nothing was recorded.');
    } finally {
      setBusy(false);
    }
  };

  const submitHomework = () => {
    const payloads: Record<string, Record<string, unknown> | undefined> = {
      none: undefined,
      link: { url: text.trim() },
      text: { text: text.trim() },
    };
    finish(payloads[submitType]);
  };

  const openFeature = (route: string) => {
    setFeatureRoute(route);
    setView('feature');
  };

  const anchor = liveRoute(module.feature);
  const anchorInPanel = hasFeaturePanel(anchor);
  // The server decides which link steps tick on opening and which wait for
  // real activity, and says so per module. The panel must not offer to mark
  // one of the latter done: that is how a dive nobody ran reached 4 of 4.
  const byActivity = Boolean(module.auto_complete);

  // Where Continue goes from the overview, by the kind of step it is.
  const start = () => {
    if (module.kind === 'link') {
      if (module.feature?.key === 'whats_allowed') {
        setView('policy');
        return;
      }
      const route = liveRoute(module.feature);
      if (hasFeaturePanel(route)) {
        openFeature(route);
        return;
      }
      if (route) {
        onClose();
        navigate(route);
        return;
      }
      finish();
      return;
    }
    if (module.kind === 'quiz') {
      openQuiz();
      return;
    }
    if (module.kind === 'homework') {
      setView('homework');
      return;
    }
    if (sections.length > 0) {
      setSection(0);
      setView('reading');
      return;
    }
    finish();
  };

  const nextSection = () => {
    if (section + 1 < sections.length) {
      setSection(section + 1);
      return;
    }
    finish();
  };

  const BODY: Record<string, React.ReactNode> = {
    overview: (
      <>
        {module.feature && (
          <Card pad="md">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[13px] leading-[21.5px] text-ink-dim">Linked work</div>
                <div className="text-[13px] font-semibold leading-[21.5px] text-ink">{module.feature.label}</div>
              </div>
              {anchorInPanel && (
                <button
                  type="button"
                  onClick={() => openFeature(anchor)}
                  className="inline-flex shrink-0 items-center gap-1.5 text-[13px] font-semibold leading-[21.5px] text-ink hover:underline"
                >
                  Open here <ArrowRight size={14} />
                </button>
              )}
              {anchor && !anchorInPanel && (
                <Link
                  to={anchor}
                  className="inline-flex shrink-0 items-center gap-1.5 text-[13px] font-semibold leading-[21.5px] text-ink hover:underline"
                >
                  Open <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </Card>
        )}

        {/* The boundary, on the screen where the work happens. A path prepares
            somebody; it is not an approval, and conflating the two is how
            unreviewed work reaches production. */}
        <p className="m-0 mt-3.5 text-[13px] leading-[21.5px] text-ink-dim">
          This path prepares people for the linked work. It does not approve or release the work.
        </p>

        {subSteps.length > 0 && (
          <div className="mt-5 overflow-hidden rounded-panel border border-line">
            <div className="flex items-center justify-between gap-3 border-b border-line-soft px-5 py-4">
              <Kicker tone="muted">Steps</Kicker>
              <span className="font-sans text-[12px] tabular-nums text-ink-dim">
                {{ true: subSteps.length, false: Math.min(1, subSteps.length) }[String(complete)]} of {subSteps.length}
              </span>
            </div>
            <ol className="m-0 list-none p-0">
              {subSteps.map((title, i) => (
                <SubStep key={`${title}-${i}`} index={i} title={title} complete={complete} />
              ))}
            </ol>
          </div>
        )}

        {subSteps.length === 0 && module.kind !== 'link' && (
          <p className="m-0 mt-5 text-[13px] leading-[21.5px] text-ink-dim">
            This step has no sub-steps recorded. Continue opens it here.
          </p>
        )}

        {module.kind === 'link' && module.feature?.key !== 'whats_allowed' && anchorInPanel && (
          <p className="m-0 mt-5 text-[13px] leading-[21.5px] text-ink-dim">
            Continue opens {module.feature?.label || 'the linked surface'} in this panel. You do
            the work here and close once, back to where you started.
          </p>
        )}

        {module.kind === 'link' && module.feature?.key !== 'whats_allowed' && anchor && !anchorInPanel && (
          <p className="m-0 mt-5 text-[13px] leading-[21.5px] text-ink-dim">
            This step is done on the {module.feature?.label || 'linked'} surface, which does not
            have a panel version yet. Continue takes you there rather than pretending the work
            happens here.
          </p>
        )}
      </>
    ),

    reading: (
      <>
        <span className="font-sans text-[12px] tabular-nums text-ink-dim">
          Section {section + 1} of {sections.length}
        </span>
        <h3 className="m-0 mt-2 text-[18px] font-bold leading-[25.4px] text-ink">
          {sections[section]?.h}
        </h3>
        <p className="m-0 mt-2.5 whitespace-pre-wrap text-[14px] leading-[23px] text-ink">
          {sections[section]?.p}
        </p>
      </>
    ),

    homework: (
      <>
        <Kicker tone="muted">What to do</Kicker>
        <p className="m-0 mt-1.5 whitespace-pre-wrap text-[14px] leading-[23px] text-ink">
          {module.instructions || 'Complete the task described by your trainer.'}
        </p>
        {submitType !== 'none' && (
          <label className="mt-5 block">
            <span className="block text-[13px] font-semibold leading-[21.5px] text-ink">
              {SUBMIT_PROMPT[submitType]}
            </span>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              className="mt-1.5 w-full resize-y rounded-act border border-input bg-white p-3.5 text-[14px] leading-[23px] text-ink outline-none transition-colors placeholder:text-ink-soft focus:border-ink"
              placeholder={SUBMIT_PROMPT[submitType]}
            />
            <span className="mt-1.5 block text-[12px] leading-[20px] text-ink-dim">
              Your response stays private to you.
            </span>
          </label>
        )}
      </>
    ),

    quiz: (
      <ol className="m-0 list-none p-0">
        {(quiz?.questions || []).map((question, qi) => (
          <li key={`${question.q}-${qi}`} className="mb-5 last:mb-0">
            <p className="m-0 text-[14px] font-semibold leading-[23px] text-ink">
              {qi + 1}. {question.q}
            </p>
            <div className="mt-2 grid gap-2">
              {question.options.map((option, oi) => (
                <label
                  key={`${option}-${oi}`}
                  className="flex cursor-pointer items-start gap-2.5 rounded-act border border-line bg-white px-3.5 py-2.5 text-[13px] leading-[21.5px] text-ink transition-colors hover:bg-canvas"
                >
                  <input
                    type="radio"
                    name={`q-${qi}`}
                    checked={answers[qi] === oi}
                    onChange={() => setAnswers({ ...answers, [qi]: oi })}
                    className="mt-1 shrink-0 border-line-strong text-ink focus:ring-ink/20"
                  />
                  <span className="min-w-0">{option}</span>
                </label>
              ))}
            </div>
          </li>
        ))}
      </ol>
    ),

    result: (
      <>
        <p className="m-0 font-sans text-[32px] font-semibold leading-none tabular-nums text-ink">
          {result?.score ?? 0}%
        </p>
        <p className="m-0 mt-2.5 text-[14px] leading-[23px] text-ink">
          {{ true: 'Passed.', false: 'Not passed yet.' }[String(Boolean(result?.passed))]}{' '}
          {result?.correct ?? 0} of {result?.total ?? 0} correct, attempt {result?.attempts ?? 1}.
        </p>
        <p className="m-0 mt-2.5 text-[13px] leading-[21.5px] text-ink-dim">
          Your score stays private to you. Only completion is shared.
        </p>
      </>
    ),

    policy: <PolicyBody />,

    done: (
      <div className="flex items-start gap-3.5">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ok-dim text-white">
          <Check size={14} strokeWidth={2.5} />
        </span>
        <p className="m-0 text-[14px] leading-[23px] text-ink">
          Recorded. This step is complete, and the list behind this panel is already updated.
        </p>
      </div>
    ),
  };

  const FOOTER: Record<string, React.ReactNode> = {
    overview: (
      <>
        <Action size="lg" onClick={start} disabled={busy} reason="Working…">
          <Spinner busy={busy} />
          {{ true: 'Review it again', false: 'Continue' }[String(complete)]}
        </Action>
        {anchor && !anchorInPanel && (
          <Action size="lg" variant="quiet" to={anchor}>
            Open linked {module.feature?.label}
          </Action>
        )}
      </>
    ),
    reading: (
      <>
        <Action size="lg" onClick={nextSection} disabled={busy} reason="Working…">
          <Spinner busy={busy} />
          {{ true: 'Finish step', false: 'Continue' }[String(section + 1 >= sections.length)]}
        </Action>
        <Action size="lg" variant="quiet" onClick={() => setView('overview')}>Back</Action>
      </>
    ),
    homework: (
      <>
        <Action size="lg" onClick={submitHomework} disabled={busy} reason="Working…">
          <Spinner busy={busy} /> Submit
        </Action>
        <Action size="lg" variant="quiet" onClick={() => setView('overview')}>Back</Action>
      </>
    ),
    quiz: (
      <>
        <Action size="lg" onClick={submitQuiz} disabled={busy} reason="Working…">
          <Spinner busy={busy} /> Submit answers
        </Action>
        <Action size="lg" variant="quiet" onClick={() => setView('overview')}>Back</Action>
      </>
    ),
    policy: (
      <>
        <Action size="lg" onClick={() => finish()} disabled={busy} reason="Working…">
          <Spinner busy={busy} /> Mark as read
        </Action>
        <Action size="lg" variant="quiet" onClick={() => setView('overview')}>Back</Action>
      </>
    ),
    feature: (
      <>
        {!byActivity && (
          <Action size="lg" onClick={() => finish()} disabled={busy} reason="Working…">
            <Spinner busy={busy} /> Mark as done
          </Action>
        )}
        {byActivity && (
          <p className="m-0 text-[13px] leading-[21.5px] text-ink-dim">
            This step ticks itself off the work above, not from opening it. Close when you are
            done and your path will have caught up.
          </p>
        )}
        <Action size="lg" variant="quiet" onClick={() => setView('overview')}>Back to the step</Action>
      </>
    ),
    result: <Action size="lg" onClick={onClose}>Close</Action>,
    done: <Action size="lg" onClick={onClose}>Close</Action>,
  };

  if (view === 'feature' && featureRoute) {
    return (
      <Modal
        onClose={onClose}
        context={module.title}
        badge={<Chip>{KIND_CHIP[module.kind] || 'Learning'}</Chip>}
        footer={<div className="flex flex-wrap items-center gap-3">{FOOTER.feature}</div>}
      >
        {error && <ErrorNote className="mb-4" message={error} />}
        <FeaturePanelBody route={featureRoute} onOpen={setFeatureRoute} onClose={onClose} />
      </Modal>
    );
  }

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>AI adoption</Kicker>}
      badge={<Chip>{KIND_CHIP[module.kind] || 'Learning'}</Chip>}
      title={module.title}
      sub={module.description}
      footer={<div className="flex flex-wrap items-center gap-3">{FOOTER[view]}</div>}
    >
      {error && <ErrorNote className="mb-4" message={error} />}
      {BODY[view]}
    </Drawer>
  );
};

/*
 * The help drawer.
 *
 * No design was supplied for this one, so it is built from the kit and lists
 * only routes that exist. It deliberately offers no support channel the
 * platform cannot deliver: every row goes somewhere real.
 */
export const HelpDrawer: React.FC<{ stepTitle: string; onClose: () => void }> = ({
  stepTitle, onClose,
}) => {
  const [view, setView] = useState('menu');
  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>AI adoption</Kicker>}
      badge={<Chip>Help</Chip>}
      title="Get help with this step"
      sub={`Two ways through, for “${stepTitle}”. Asking is not a mark against you: a step nobody can finish is a problem with the step.`}
      footer={(
        <div className="flex flex-wrap items-center gap-3">
          {view === 'policy' && (
            <Action size="lg" variant="quiet" onClick={() => setView('menu')}>Back</Action>
          )}
        </div>
      )}
    >
      {view === 'policy' && <PolicyBody />}
      {view === 'menu' && (
        <div className="overflow-hidden rounded-panel border border-line">
          <button
            type="button"
            onClick={() => setView('policy')}
            className="flex w-full items-start gap-3.5 border-b border-line-soft px-5 py-4 text-left transition-colors hover:bg-canvas"
          >
            <span className="grid h-[37px] w-[37px] shrink-0 place-items-center rounded-tile bg-canvas-inset text-ink-muted">
              <ShieldCheck size={16} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold leading-[21.5px] text-ink">What is allowed</span>
              <span className="block text-[13px] leading-[21.5px] text-ink-dim">
                The boundary for your role, in the words your company approved. Opens here.
              </span>
            </span>
            <ArrowRight size={16} className="mt-1 shrink-0 text-ink-soft" />
          </button>
          {/* Orbit is the one deliberate full-screen flow, so this row is the
              one that leaves the panel. */}
          <Link
            to="/orbit/request"
            onClick={onClose}
            className="flex items-start gap-3.5 px-5 py-4 transition-colors hover:bg-canvas"
          >
            <span className="grid h-[37px] w-[37px] shrink-0 place-items-center rounded-tile bg-accent-500 text-ink">
              <MessageSquare size={16} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold leading-[21.5px] text-ink">Ask Orbit</span>
              <span className="block text-[13px] leading-[21.5px] text-ink-dim">
                Describe what is in the way. Orbit routes it and tells you why.
              </span>
            </span>
            <ArrowRight size={16} className="mt-1 shrink-0 text-ink-soft" />
          </Link>
        </div>
      )}
    </Drawer>
  );
};
